// ============================================================================
// CANCUN100 — edadesProducto.js  (APP 2/33 · RP-12: edades por producto y personas internas por bloque)
// ----------------------------------------------------------------------------
// Cada proveedor define distinto quién es infante, menor o adulto. Los rangos se leen por sku de
// cancun100_productos (la misma fuente que la web) y, si ahí no hay dato, de descriptivos_contenido.
// Con las edades de los niños del grupo se propone el reparto de cada bloque de tour o actividad individual.
// Hoteles, combos y paquetes no se reparten (en los combos el rango es solo informativo).
// ============================================================================
import { useEffect, useState } from 'react';

// Con true, App.js guarda también reservas.edades_ninos / cotizaciones.edades_ninos. APP 2/36: la columna ya existe (Claude aplicó app2_33_edades_ninos).
export const EDADES_EN_COLUMNA = true;

const COLS = 'sku,edad_infante_max,edad_menor_min,edad_menor_max,edad_adulto_min';
const cache = new Map(); // sku → rango | null (null = sin dato en ninguna de las dos fuentes)
const num = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v)) ? null : Number(v));
const aRango = (r, fuente) => {
  const x = { inf: num(r.edad_infante_max), mmin: num(r.edad_menor_min), mmax: num(r.edad_menor_max), amin: num(r.edad_adulto_min), gratis: r.infante_gratis === true, tipo: r.tipo_producto || null, fuente };
  return (x.inf != null || x.mmin != null) && (x.mmax != null || x.amin != null) ? x : null;
};

export async function cargarEdades(supabase, skus) {
  const faltan = [...new Set((skus || []).filter((s) => s && !cache.has(s)))];
  if (!faltan.length || !supabase) return;
  const tipos = new Map();
  try {
    const { data } = await supabase.from('cancun100_productos').select(COLS + ',infante_gratis,tipo_producto').in('sku', faltan);
    (data || []).forEach((r) => { tipos.set(r.sku, r.tipo_producto || null); const x = aRango(r, 'productos'); if (x) cache.set(r.sku, x); });
  } catch (e) { /* sin red: se intenta la siguiente fuente */ }
  const sinDato = faltan.filter((s) => !cache.has(s));
  if (sinDato.length) {
    try {
      const { data } = await supabase.from('descriptivos_contenido').select(COLS).in('sku', sinDato);
      (data || []).forEach((r) => { const x = aRango({ ...r, tipo_producto: tipos.get(r.sku) }, 'descriptivos'); if (x) cache.set(r.sku, x); });
    } catch (e) { /* idem */ }
  }
  // Lo que no tiene rango queda como «por confirmar», pero conserva su tipo (combo/paquete).
  faltan.forEach((s) => { if (!cache.has(s)) cache.set(s, tipos.get(s) ? { sinRango: true, tipo: tipos.get(s) } : null); });
}

// Mapa sku → rango, que se actualiza cuando llegan los datos.
export function useEdadesProductos(supabase, skus) {
  const clave = [...new Set((skus || []).filter(Boolean))].sort().join('|');
  const [ver, setVer] = useState(0);
  useEffect(() => {
    let vivo = true;
    const lista = clave ? clave.split('|') : [];
    if (lista.some((s) => !cache.has(s))) cargarEdades(supabase, lista).then(() => { if (vivo) setVer((v) => v + 1); });
    return () => { vivo = false; };
  }, [supabase, clave]);
  const m = {};
  (clave ? clave.split('|') : []).forEach((s) => { if (cache.has(s)) m[s] = cache.get(s); });
  m.__v = ver; m.__listo = (clave ? clave.split('|') : []).every((s) => cache.has(s));
  return m;
}

export const tieneRango = (r) => !!(r && !r.sinRango);
// Combos y paquetes (producto que ya es un combo/paquete): el rango es solo informativo.
export const esComboOPaquete = (product, r) => !!(product?.es_combo || ['combo', 'paquete', 'agrupado'].includes(r?.tipo || product?.tipo_producto));

// APP 2/38: «Menor 5–11 · Infante 0–4 · Adulto 12+» tal cual; sin rango: «Edades por confirmar» (nunca «tentativas» ni «sugerido»).
export function textoRango(r) {
  if (!tieneRango(r)) return 'Edades por confirmar';
  const infMax = r.inf != null ? r.inf : r.mmin - 1;
  const menMin = r.mmin != null ? r.mmin : r.inf + 1;
  const menMax = r.mmax != null ? r.mmax : r.amin - 1;
  const adMin = r.amin != null ? r.amin : r.mmax + 1;
  return `Menor ${menMin}–${menMax} · Infante 0–${infMax}${r.gratis ? ' (gratis)' : ''} · Adulto ${adMin}+`;
}

// APP 2/38: las edades aplican SOLO a tours individuales: unidad_tipo 'pax', fuera de un combo (es_combo) y que el producto no sea
// combo/paquete. No aplican a promociones ('promo'), bloques ('bloque': yates, privados, transporte), combos ni hoteles.
// Nota: `aplica_combo` solo dice que el tour PUEDE ir en combo (Xcaret Básico lo tiene); vendido suelto sí lleva edades.
export const aplicaEdades = (product, r) => !!product && (product.unidad_tipo || 'pax') === 'pax' && !product.es_combo && !product.es_custom && !['combo', 'paquete', 'agrupado'].includes(r?.tipo || product.tipo_producto);

export function clase(r, edad) {
  const infMax = r.inf != null ? r.inf : r.mmin - 1;
  const adMin = r.amin != null ? r.amin : r.mmax + 1;
  if (edad <= infMax) return 'infantes';
  if (edad >= adMin) return 'adultos';
  return 'menores';
}

// Edades válidas (0–17) capturadas; null si falta alguna (no se sugiere nada con datos a medias).
export function edadesCompletas(edades, nNinos) {
  if (!nNinos) return null;
  const v = (edades || []).slice(0, nNinos).map((e) => (e === '' || e === null || e === undefined ? NaN : Number(e)));
  if (v.length < nNinos || v.some((e) => !Number.isInteger(e) || e < 0 || e > 17)) return null;
  return v;
}

// Reparto del bloque según su rango: { adultos, menores, infantes, porEdad: [{edad, clase}] }
export function repartir(r, nAdultos, edades) {
  if (!tieneRango(r) || !edades) return null;
  const out = { adultos: nAdultos, menores: 0, infantes: 0, porEdad: [] };
  edades.forEach((e) => { const c = clase(r, e); out[c] += 1; out.porEdad.push({ edad: e, clase: c }); });
  return out;
}

const NOMBRE = { infantes: ['infante', 'infantes'], menores: ['menor', 'menores'], adultos: ['adulto', 'adultos'] };
const lista = (xs) => (xs.length === 1 ? String(xs[0]) : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`);
// «En este tour, el niño de 3 años cuenta como menor» (solo si el reparto no coincide con el grupo capturado).
export function avisoReparto(rep, grupo) {
  if (!rep) return null;
  const partes = [];
  ['adultos', 'menores', 'infantes'].forEach((c) => {
    const extra = rep[c] - (grupo[c] || 0);
    if (extra <= 0) return;
    let xs = rep.porEdad.filter((p) => p.clase === c).map((p) => p.edad).sort((a, b) => a - b);
    // Los que cambian de grupo son los que están cerca de la frontera.
    if (c === 'infantes') xs = xs.slice(-extra);
    else if (c === 'adultos') xs = xs.slice(0, extra);
    else xs = (rep.infantes < (grupo.infantes || 0)) ? xs.slice(0, extra) : xs.slice(-extra);
    if (!xs.length) return;
    partes.push(xs.length === 1 ? `el niño de ${xs[0]} años cuenta como ${NOMBRE[c][0]}` : `los niños de ${lista(xs)} años cuentan como ${NOMBRE[c][1]}`);
  });
  return partes.length ? `En este tour, ${partes.join('; ')}.` : null;
}

// APP 2/38: «el de 4 va como infante; los de 5 y 6 van como menores».
export function describirReparto(rep) {
  if (!rep || !rep.porEdad || !rep.porEdad.length) return null;
  const partes = [];
  ['infantes', 'menores', 'adultos'].forEach((c) => {
    const xs = rep.porEdad.filter((p) => p.clase === c).map((p) => p.edad).sort((a, b) => a - b);
    if (!xs.length) return;
    partes.push(xs.length === 1 ? `el de ${xs[0]} va como ${NOMBRE[c][0]}` : `los de ${lista(xs)} van como ${NOMBRE[c][1]}`);
  });
  return partes.length ? partes.join('; ') : null;
}
// Desglose interno (PDF de comisión): «2 adultos · 1 menor (10) · 1 infante (3)». El cliente nunca ve edades.
export function desgloseEdades(a, m, i, porEdad) {
  const eds = (c) => (porEdad || []).filter((p) => p.clase === c).map((p) => p.edad).sort((x, y) => x - y);
  const pz = (n, uno, varios, c) => { if (!n) return null; const e = c ? eds(c) : []; return `${n} ${n === 1 ? uno : varios}${e.length ? ` (${e.join(', ')})` : ''}`; };
  return [pz(a, 'adulto', 'adultos', null), pz(m, 'menor', 'menores', 'menores'), pz(i, 'infante', 'infantes', 'infantes')].filter(Boolean).join(' · ');
}
// ¿El reparto del bloque contradice las edades y el rango del tour? Devuelve el reparto que corresponde o null.
export function contradice(r, bloque, edades) {
  if (!tieneRango(r) || !edades) return null;
  const rp = repartir(r, parseInt(bloque.adultos) || 0, edades);
  return (rp.menores !== (parseInt(bloque.menores) || 0) || rp.infantes !== (parseInt(bloque.infantes) || 0)) ? rp : null;
}
